import { inject } from '@angular/core';
import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom } from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type FieldSortConfig,
  fixedListLayoutMode,
  listColumnsApply,
  parseBorderRadius,
  type PropertiesConfigInputListCustomFieldModel,
  type PropertiesConfigInputListFieldModel,
  type PropertiesConfigInputListFiltersModel,
  resolveListLayoutFields,
  toSortRequest,
} from '@blaze-shared/elements-domain';
import { NONE_OPTION_VALUE, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import {
  type ContainerFieldFilter,
  type ContainerFieldMapping,
  type DefinitionMapper,
  resolveFilterType,
  toActionsDisplayModeStyle,
  toBulkActionsDisplayModeStyle,
  toRecordClickIntent,
} from '../../../ai';
import { HIDE_WHEN_NO_DATA } from '../../../ai/common/hide-when-empty';
import { toWidthStyle } from '../../../ai/models/styles';
import { SearchAndFiltersWrapperService } from '../../../services/search-and-filters-wrapper.service';
import { type ListImagePropertiesModel } from '../list.models';

import {
  toIconIndicatorAlign,
  toListBehavior,
  toListEmptyImageState,
  toListImageBehavior,
  toListImageShape,
  toListMobileBehavior,
  toListTemplateKind,
  toSortByEntry,
} from './list.appearance';
import {
  type ListDefinition,
  type ListTemplate,
  type ListTemplateImageOptions,
  type SortByEntry,
} from './list.definition';
import { type ListFieldSlot, SLOT_TO_FIELD_KEY } from './list.slots';

type ListTemplateImageOverrides = Partial<ListTemplateImageOptions>;

export class ListDefinitionMapper implements DefinitionMapper<'list'> {
  private readonly searchAndFiltersWrapperService = inject(SearchAndFiltersWrapperService);

  readonly type = 'list';

  extract(element: CanvasElementFrom<'list'>): PartialDeep<ListDefinition> {
    const content = element.props.content;

    const itemActions = content.unifiedRowActions?.map((action) => ({
      $type: action.type,
      name: action.label,
      iconName: action.icon,
      aiDescription: action.aiDescription,
    }));

    const bulkActions = content.unifiedBulkActions?.map((action) => ({
      $type: action.type,
      name: action.label,
      iconName: action.icon,
      aiDescription: action.aiDescription,
    }));

    const fieldFilters = content.searchAndFilters?.filters
      ?.map((filter) => this.toFieldFilter(element, filter))
      .filter((filter): filter is ContainerFieldFilter => !!filter);

    const list: PartialDeep<ListDefinition> = {
      $type: 'list',
      title: content.title?.label,
      showTitle: content.title?.enabled ?? false,
      search: content.searchAndFilters?.searchEnabled,
      customTemplateCode: content.list?.customTemplate?.code,
      width: toWidthStyle(element.width),
      emptyMessage: content.noDataMessage,
      hideWhenEmpty: content.visible?.internalStates?.includes(HIDE_WHEN_NO_DATA) ?? false,
    };

    const direction = content.list?.layout?.direction;

    if (direction) {
      list.template = this.getListTemplate(element);

      if (!fixedListLayoutMode(direction) && content.list?.layout?.mode) {
        list.behavior = toListBehavior(content.list.layout.mode);
      }

      if (direction !== 'notification' && content.mobile?.scrollMode) {
        list.mobileBehavior = toListMobileBehavior(content.mobile.scrollMode);
      }

      if (
        resolveListLayoutFields(direction, content.list?.layout?.mode).rowsApply &&
        content.list?.layout?.rows !== undefined
      ) {
        list.rows = content.list.layout.rows;
      }

      if (listColumnsApply(direction) && content.list?.layout?.cols !== undefined) {
        list.columns = content.list.layout.cols;
      }
    }

    if (itemActions?.length) {
      list.itemActions = itemActions;
    }

    if (content.unifiedRowActions?.length && content.actions?.mode) {
      list.itemActionsDisplayMode = toActionsDisplayModeStyle(content.actions.mode);
    }

    if (content.rowClick?.enabled) {
      list.itemClick = toRecordClickIntent(content.rowClick.action);
    }

    if (bulkActions?.length) {
      list.bulkActions = bulkActions;
    }

    if (content.unifiedBulkActions?.length && content.list?.bulkActionsDisplayMode) {
      list.bulkActionsDisplayMode = toBulkActionsDisplayModeStyle(
        content.list.bulkActionsDisplayMode,
      );
    }

    if (content.itemAdd?.enabled) {
      list.addNewItem = { label: content.itemAdd.label };
    }

    if (content.indicator?.enabled && content.indicator.icon) {
      list.iconIndicator = {
        enabled: true,
        iconName: content.indicator.icon,
        align: toIconIndicatorAlign(content.indicator.align),
      };
    }

    if (fieldFilters?.length) {
      list.fieldFilters = fieldFilters;
    }

    const sortBy = this.toSortBy(content.sort);
    if (sortBy.length) {
      list.sortBy = sortBy;
    }

    const fieldMappings = this.toFieldMappings(content.fields, content.customFields);
    if (fieldMappings.length) {
      list.fieldMappings = fieldMappings;
    }

    return list;
  }

  private toFieldMappings(
    fields: PropertiesConfigInputListFieldModel | undefined,
    customFields: PropertiesConfigInputListCustomFieldModel[] | undefined,
  ): ContainerFieldMapping<ListFieldSlot>[] {
    const slotMappings = (
      Object.entries(SLOT_TO_FIELD_KEY) as [
        ListFieldSlot,
        keyof PropertiesConfigInputListFieldModel,
      ][]
    )
      .map(([slot, key]) => ({ slot, field: fields?.[key] }))
      .filter((entry): entry is { slot: ListFieldSlot; field: string } => !!entry.field)
      .map(({ slot, field }) => ({ label: '', field, mapping: slot }));

    const customFieldMappings: ContainerFieldMapping<ListFieldSlot>[] = (customFields ?? [])
      .filter((customField) => !!customField.column)
      .map((customField) => ({
        label: customField.label,
        field: customField.column ?? '',
        mapping: null,
      }));

    return [...slotMappings, ...customFieldMappings];
  }

  private toSortBy(sort: FieldSortConfig | FieldSortConfig[] | undefined): SortByEntry[] {
    const entries = Array.isArray(sort) ? sort : sort ? [sort] : [];

    return entries
      .map(toSortRequest)
      .map(toSortByEntry)
      .filter((entry): entry is SortByEntry => !!entry && entry.field !== NONE_OPTION_VALUE);
  }

  private toFieldFilter(
    element: CanvasElementFrom<'list'>,
    filter: PropertiesConfigInputListFiltersModel,
  ): ContainerFieldFilter | undefined {
    if (!filter.filterField) {
      return undefined;
    }

    return {
      label: filter.placeholder ?? '',
      field: filter.filterField,
      filterType: resolveFilterType({
        searchAndFiltersWrapperService: this.searchAndFiltersWrapperService,
        containerElementId: element.id,
        filter,
      }),
    };
  }

  private getListTemplate(
    element: CanvasElementFrom<'list'>,
  ): PartialDeep<ListTemplate> | undefined {
    const direction = element.props.content.list?.layout?.direction;

    if (!direction) {
      return undefined;
    }

    const imageOverrides =
      direction === 'custom' ? {} : this.toImageOverrides(element.props.style.image);

    if (direction === 'subscription-plan') {
      const bulletIconName = element.props.content.subscriptionPlan?.bulletIcon;
      return {
        $type: 'subscription',
        ...(bulletIconName ? { bulletIconName } : {}),
        ...imageOverrides,
      };
    }
    return {
      $type: toListTemplateKind(direction),
      ...(direction !== 'custom' ? imageOverrides : {}),
    };
  }

  private toImageOverrides(
    image: ListImagePropertiesModel | undefined,
  ): ListTemplateImageOverrides {
    if (!image) {
      return {};
    }

    const overrides: ListTemplateImageOverrides = {};
    const imageShape = image.shape ? toListImageShape(image.shape) : undefined;

    if (imageShape) {
      overrides.imageShape = imageShape;
    }

    if (image.behavior) {
      overrides.imageBehavior = toListImageBehavior(image.behavior);
    }

    const radius = toRadiusPixelValue(image.radius);
    if (radius !== undefined) {
      overrides.radius = radius;
    }

    const width = parsePixelValue(image.outlineWidth);
    if (width !== undefined) {
      overrides.width = width;
    }

    if (image.emptyThumbnail) {
      overrides.emptyImageState = toListEmptyImageState(image.emptyThumbnail);
    }

    return overrides;
  }
}

function parsePixelValue(value: ValueWithUnitOfMeasure | undefined): number | undefined {
  const match = typeof value === 'string' ? /^(-?\d+(?:\.\d+)?)px$/.exec(value) : null;
  return match ? Number(match[1]) : undefined;
}

function toRadiusPixelValue(value: BorderRadiusValue | undefined): number | undefined {
  if (!value || value.startsWith('$t.')) {
    return undefined;
  }
  return parseBorderRadius(value).topLeft;
}
